import React, { useMemo, useState } from 'react';
import { Plane, Search } from 'lucide-react';
import { Button, Input, Select, Badge, Spinner, Card, CardContent } from '@/components/ui';
import { LocationAutocomplete, type LocationData } from '@/components/ui/LocationAutocomplete/LocationAutocomplete';
import { AssessmentsService } from '@/services/AssessmentsService';
import { FlightOffersService, type OfferSummary } from '@/services/FlightOffersService';
import { useNotificationStore } from '@/stores/notificationStore';

interface Props {
  onClose: () => void;
}

export function QuickEstimateModal({ onClose }: Props) {
  const assessmentsService = useMemo(() => new AssessmentsService(), []);
  const offersService = useMemo(() => new FlightOffersService(), []);
  const notifyError = useNotificationStore(s => s.error);

  const [originInput, setOriginInput] = useState('');
  const [destinationInput, setDestinationInput] = useState('');
  const [origin, setOrigin] = useState<LocationData | null>(null);
  const [destination, setDestination] = useState<LocationData | null>(null);
  const [originAirports, setOriginAirports] = useState<{ iata: string; name: string }[]>([]);
  const [destinationAirports, setDestinationAirports] = useState<{ iata: string; name: string }[]>([]);
  const [selectedOriginIata, setSelectedOriginIata] = useState<string>('');
  const [selectedDestinationIata, setSelectedDestinationIata] = useState<string>('');
  const [departDate, setDepartDate] = useState<string>('');
  const [returnDate, setReturnDate] = useState<string>('');
  const [cabinClass, setCabinClass] = useState<'economy' | 'premium_economy' | 'business' | 'first'>('economy');
  const [maxConnections, setMaxConnections] = useState<0 | 1 | 2>(1);
  const [loadingAirports, setLoadingAirports] = useState(false);
  const [searching, setSearching] = useState(false);
  const [offers, setOffers] = useState<OfferSummary[] | null>(null);

  async function resolveNearestAirports(loc: LocationData, setter: (a: { iata: string; name: string }[]) => void, selectSetter: (iata: string) => void) {
    setLoadingAirports(true);
    try {
      const airports = await assessmentsService.resolveDestinationAirports(loc.lat, loc.lon, 5);
      const filtered = (airports || []).map(a => ({ iata: a.iata, name: a.name }));
      setter(filtered);
      if (filtered[0]?.iata) selectSetter(filtered[0].iata);
    } catch (e: any) {
      notifyError(e?.message || 'Failed to resolve nearby airports');
    } finally {
      setLoadingAirports(false);
    }
  }

  const onOriginSelect = (loc: LocationData) => {
    setOrigin(loc);
    resolveNearestAirports(loc, setOriginAirports, setSelectedOriginIata);
  };
  const onDestinationSelect = (loc: LocationData) => {
    setDestination(loc);
    resolveNearestAirports(loc, setDestinationAirports, setSelectedDestinationIata);
  };

  const canSearch = !!selectedOriginIata && !!selectedDestinationIata && !!departDate && !!returnDate;

  async function handleSearch() {
    if (!canSearch) return;
    setSearching(true);
    setOffers(null);
    try {
      const result = await offersService.searchRoundTrip({
        origin: selectedOriginIata,
        destination: selectedDestinationIata,
        departDate,
        returnDate,
        cabinClass,
        maxConnections,
        sort: 'total_amount',
        limit: 20,
      });
      setOffers(result.offers);
    } catch (e: any) {
      notifyError(e?.message || 'Search failed');
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Origin city</label>
          <LocationAutocomplete
            value={originInput}
            onChange={setOriginInput}
            onLocationSelect={onOriginSelect}
            placeholder="Search origin city"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Destination city</label>
          <LocationAutocomplete
            value={destinationInput}
            onChange={setDestinationInput}
            onLocationSelect={onDestinationSelect}
            placeholder="Search destination city"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Origin airport</label>
          <Select value={selectedOriginIata} onChange={(e) => setSelectedOriginIata(e.target.value)}>
            {originAirports.map(a => (
              <option key={a.iata} value={a.iata}>{a.iata} — {a.name}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Destination airport</label>
          <Select value={selectedDestinationIata} onChange={(e) => setSelectedDestinationIata(e.target.value)}>
            {destinationAirports.map(a => (
              <option key={a.iata} value={a.iata}>{a.iata} — {a.name}</option>
            ))}
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Depart date</label>
          <Input type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Return date</label>
          <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Cabin class</label>
          <Select value={cabinClass} onChange={(e) => setCabinClass(e.target.value as any)}>
            <option value="economy">Economy</option>
            <option value="premium_economy">Premium Economy</option>
            <option value="business">Business</option>
            <option value="first">First</option>
          </Select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Max connections</label>
          <Select value={String(maxConnections)} onChange={(e) => setMaxConnections(Number(e.target.value) as any)}>
            <option value="0">Direct only</option>
            <option value="1">Up to 1 stop</option>
            <option value="2">Up to 2 stops</option>
          </Select>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button leftIcon={<Search className="w-4 h-4" />} disabled={!canSearch || loadingAirports || searching} onClick={handleSearch}>
          {searching ? 'Searching…' : 'Estimate'}
        </Button>
        <Button variant="ghost" onClick={onClose}>Close</Button>
        {loadingAirports && <Spinner size="sm" />}
      </div>

      {offers && (
        <div className="space-y-3">
          {offers.length === 0 && (
            <div className="text-sm text-muted-foreground">No offers found.</div>
          )}
          {offers.map((off) => (
            <Card key={off.offer_id}>
              <CardContent>
                <div className="flex items-start justify-between gap-4">
                  <div className="space-y-1">
                    <div className="text-lg font-semibold">{off.price.currency} {off.price.amount}</div>
                    <div className="text-sm text-muted-foreground">{Math.round(off.total_duration_min / 60)}h {off.total_duration_min % 60}m total</div>
                    <div className="flex flex-col gap-1">
                      {off.slices.map((s, idx) => (
                        <div key={idx} className="text-sm">
                          <span className="font-medium">{s.origin}</span> → <span className="font-medium">{s.destination}</span>
                          <span className="ml-2 text-muted-foreground">{new Date(s.departing_at || '').toLocaleString()} → {new Date(s.arriving_at || '').toLocaleString()}</span>
                          <span className="ml-2"><Badge variant="secondary">{s.stops} stops</Badge></span>
                          {s.marketing && s.marketing.length > 0 && (
                            <div className="text-xs text-muted-foreground mt-1">
                              {s.marketing.map((m, i) => (
                                <span key={i} className="mr-2">
                                  {(m.marketing_iata || '')} {(m.flight_number || '')}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Plane className="w-4 h-4" /> {off.owner || 'Airline'}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}


